import { Marked, type Token, type Tokens } from "marked";
import { slugify } from "@/src/common/utils/slug.utils";

export interface LegalHeading {
	id: string;
	text: string;
}

export interface LegalDocument {
	html: string;
	headings: LegalHeading[];
}

export interface LegalSection {
	id: string;
	title: string;
	html: string;
}

const SECTION_DEPTH = 2;

const isHeading = (token: Token): token is Tokens.Heading => token.type === "heading";

const isSectionHeading = (token: Token): token is Tokens.Heading => isHeading(token) && token.depth === SECTION_DEPTH;

const legalMarked = new Marked({
	renderer: {
		heading(token) {
			const content = this.parser.parseInline(token.tokens);
			if (token.depth !== SECTION_DEPTH) {
				return `<h${token.depth}>${content}</h${token.depth}>\n`;
			}
			const id = slugify(token.text);
			return `<h${token.depth} id="${id}"><a class="legal-anchor" href="#${id}">${content}</a></h${token.depth}>\n`;
		},
	},
});

export const parseLegalDocument = (markdown: string): LegalDocument => {
	const tokens = legalMarked.lexer(markdown);
	const headings = tokens.filter(isSectionHeading).map((token) => ({ id: slugify(token.text), text: token.text }));
	return { html: legalMarked.parser(tokens), headings };
};

export const splitLegalSections = (markdown: string): LegalSection[] => {
	const tokens = legalMarked.lexer(markdown);
	const sections: LegalSection[] = [];
	let current: { id: string; title: string; body: Token[] } | null = null;

	for (const token of tokens) {
		if (isSectionHeading(token)) {
			if (current) {
				sections.push({ id: current.id, title: current.title, html: legalMarked.parser(current.body) });
			}
			current = { id: slugify(token.text), title: token.text, body: [] };
			continue;
		}
		current?.body.push(token);
	}

	if (current) {
		sections.push({ id: current.id, title: current.title, html: legalMarked.parser(current.body) });
	}

	return sections;
};
